/* eslint-disable react/jsx-indent */
import { List, ListItem, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import SidebarItem from 'components/Layouts/components/SidebarItem';
import * as urls from 'routes/config';
import { SupportedProvider, SupportedTab } from '../utils/types';

interface ProviderOption {
  label: string;
  value: string;
}

interface Props {
  providers: ProviderOption[];
  tab: SupportedTab;
  isLoading?: boolean;
}

const NETSAPIENS_ROUTE_SEGMENT = 'netsapiens';

const providerToRouteSegment = (provider: SupportedProvider) =>
  provider === 'netsapiens' ? NETSAPIENS_ROUTE_SEGMENT : provider;

const toTelephonyPath = (provider: SupportedProvider, tab: SupportedTab) =>
  `/agency/${urls.agency.telephony}/${providerToRouteSegment(provider)}/${tab}`;

export default function ProviderSidebar({ providers, tab, isLoading = false }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const skeletonItems = Array.from({ length: 4 }, (_, index) => index);

  return (
    <>
      {!isMediumScreen ? (
        <List
          sx={{
            minWidth: { md: 162, lg: 200 },
            maxWidth: { md: 200, lg: 262 },
            height: 'fit-content',
            background: theme.palette.background.paper,
            borderRadius: 2,
            position: 'sticky',
            top: { md: '120px', lg: '200px' },
            flexShrink: 0
          }}
        >
          {isLoading
            ? skeletonItems.map((index) => (
                <ListItem key={`provider-skeleton-${index}`}>
                  <Skeleton variant='rounded' height={32} width='100%' />
                </ListItem>
              ))
            : providers.map((item) => (
                <SidebarItem
                  key={item.value}
                  to={toTelephonyPath(item.value as SupportedProvider, tab)}
                  label={item.label}
                />
              ))}
        </List>
      ) : (
        <List
          sx={{
            display: 'flex',
            flexDirection: 'row',
            height: 'fit-content',
            background: theme.palette.background.paper,
            borderRadius: '12px',
            position: 'fixed',
            bottom: '0px',
            left: { xs: '12px', sm: '24px' },
            right: { xs: '12px', sm: '24px' },
            overflowX: 'auto',
            overflowY: 'hidden',
            zIndex: 1000,
            backgroundColor: theme.palette.background.default,
            padding: { xs: '8px', sm: '12px' },
            gap: { xs: 1, sm: 1.5 },
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {isLoading
            ? skeletonItems.map((index) => (
                <ListItem key={`provider-mobile-skeleton-${index}`} sx={{ p: 0 }}>
                  <Skeleton variant='rounded' height={28} width={90} />
                </ListItem>
              ))
            : providers.map((item) => (
                <SidebarItem
                  key={item.value}
                  type='mobile'
                  to={toTelephonyPath(item.value as SupportedProvider, tab)}
                  label={item.label}
                />
              ))}
        </List>
      )}
    </>
  );
}
